import { type ReactElement, useEffect, useMemo, useState } from "react";

import {
  APIError,
  api,
  type AuditEvent,
  type Organization,
} from "../api/client";

type SeverityFilter = "all" | AuditEvent["severity"];

export function Audit({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [severity, setSeverity] = useState<SeverityFilter>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<APIError | Error | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([
      api.organization(organizationId),
      api.auditEvents(organizationId),
    ])
      .then(([organizationResult, page]) => {
        if (!active) return;
        setOrganization(organizationResult);
        setEvents(page.items);
        setLoading(false);
      })
      .catch((cause: APIError | Error) => {
        if (!active) return;
        setError(cause);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [organizationId]);

  const visibleEvents = useMemo(
    () =>
      events.filter(
        (event) => severity === "all" || event.severity === severity,
      ),
    [events, severity],
  );

  if (error) {
    const notFound = error instanceof APIError && error.code === "not_found";
    return (
      <main className="page">
        <section className="empty-state error-state" role="alert">
          <p className="eyebrow">{notFound ? "Not found" : "Unable to load"}</p>
          <h1>
            {notFound
              ? "This audit trail is unavailable."
              : "We couldn’t load the audit trail."}
          </h1>
          <p>
            {notFound
              ? "The organization may not exist, or your membership has changed."
              : error.message}
          </p>
          <a className="button button-secondary" href="/workspace">
            Return to documents
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="page audit-page">
      <div className="breadcrumbs">
        <a href="/workspace">Documents</a>
        <span aria-hidden="true">/</span>
        <span>Audit trail</span>
      </div>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{organization?.name ?? "Organization"}</p>
          <h1>Audit trail</h1>
          <p>
            Append-only security and collaboration events, attributed to their
            initiating actor.
          </p>
        </div>
        <label className="filter-control">
          <span>Severity</span>
          <select
            value={severity}
            onChange={(event) =>
              setSeverity(event.target.value as SeverityFilter)
            }
          >
            <option value="all">All severities</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="high">High</option>
          </select>
        </label>
      </header>

      {loading ? (
        <section
          className="audit-list"
          aria-label="Loading audit events"
          aria-busy="true"
        >
          {[0, 1, 2].map((value) => (
            <div className="audit-row skeleton" key={value} />
          ))}
        </section>
      ) : visibleEvents.length === 0 ? (
        <section className="empty-state">
          <h2>No matching events.</h2>
          <p>Try another severity filter.</p>
        </section>
      ) : (
        <section className="audit-list" aria-label="Audit events">
          {visibleEvents.map((event) => (
            <AuditRow event={event} key={event.id} />
          ))}
        </section>
      )}
    </main>
  );
}

function AuditRow({ event }: { event: AuditEvent }): ReactElement {
  return (
    <article className="audit-row">
      <span
        className={`severity-marker severity-${event.severity}`}
        aria-label={`${event.severity} severity`}
      />
      <div className="audit-action">
        <strong>{humanize(event.action)}</strong>
        <span>
          {event.target_type}
          {event.target_id ? ` · ${event.target_id.slice(0, 8)}` : ""}
        </span>
      </div>
      <div className="audit-actor">
        <span className="avatar avatar-small">
          {initials(event.actor.display_name)}
        </span>
        <span>
          {event.actor.display_name}
          <small>{event.actor.email}</small>
        </span>
      </div>
      <time dateTime={event.occurred_at}>
        {new Date(event.occurred_at).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </time>
      <code title={event.request_id}>{event.request_id.slice(0, 10)}…</code>
    </article>
  );
}

function humanize(value: string): string {
  return value
    .replaceAll("_", " ")
    .replaceAll(".", " · ")
    .replace(/^./, (first) => first.toUpperCase());
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
}
